import {
  Component, Input, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, Inject, ElementRef, HostListener, ViewContainerRef, ComponentRef, ViewChild,
} from '@angular/core';
import { Subject } from 'rxjs';
import { SocialSharingPopoverComponent } from './social-sharing-popover.component';
import { SocialSharingUtilsService } from './social-sharing-utils.service';

@Component({
  selector: 'ut-social-sharing',
  templateUrl: './social-sharing.component.html',
  styleUrls: ['./social-sharing.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SocialSharingComponent implements OnInit, OnDestroy {
  @Input() target!: string;
  @Input() text!: string;
  @Input() isPopover: boolean = false;
  @Input() type: string = '';

  @ViewChild('popoverContainer', { read: ViewContainerRef }) popoverContainer!: ViewContainerRef;

  isPopoverOpen = false;
  isLightTester = false;
  shareText = '';

  private destroy$ = new Subject<void>();
  private componentRef?: ComponentRef<SocialSharingPopoverComponent>;

  constructor(
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef,
    private socialSharingUtils: SocialSharingUtilsService,
    @Inject('UserService') private UserService: any,
    @Inject('LightTesterErrorService') private LightTesterErrorService: any,
  ) {}

  ngOnInit(): void {
    this.shareText = `${this.text} - via @uTest`;

    if (this.UserService) {
      this.UserService.me().then((res: any) => {
        this.isLightTester = res?.data?.is_light_tester;
        this.cdr.markForCheck();
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.closePopover();
  }

  openPopover(): void {
    if (this.isPopoverOpen || !this.popoverContainer) return;

    this.componentRef = this.popoverContainer.createComponent(SocialSharingPopoverComponent);

    // Pass data to popover component
    this.componentRef.instance.target = this.target;
    this.componentRef.instance.text = this.shareText;
    this.componentRef.instance.isLightTester = this.isLightTester;
    this.componentRef.instance.checkLightTester = this.checkLightTester.bind(this);

    this.isPopoverOpen = true;
    this.cdr.markForCheck();

    // Setup focus management after view init
    setTimeout(() => this.setupFocusManagement(), 100);

    // Setup click outside listener
    setTimeout(() => this.setupClickOutsideListener(), 0);
  }

  closePopover(): void {
    // No-op when there's nothing open — otherwise this reschedules a delayed
    // refocus onto the trigger button from ngOnDestroy for every card in a
    // reloaded list, stealing focus back from wherever it was legitimately
    // restored to (e.g. a filter dropdown cleared via FLO-51406's fix).
    if (!this.isPopoverOpen) return;

    if (this.componentRef) {
      this.componentRef.destroy();
      this.componentRef = undefined;
    }
    if (this.popoverContainer) {
      this.popoverContainer.clear();
    }
    this.isPopoverOpen = false;
    this.cdr.markForCheck();

    // Remove click outside listener
    this.removeClickOutsideListener();

    // Return focus to trigger button
    setTimeout(() => {
      const button = this.elementRef.nativeElement.querySelector('button');
      if (button) button.focus();
    }, 100);
  }

  private setupFocusManagement(): void {
    if (!this.componentRef) return;

    const popoverElement = this.componentRef.location.nativeElement;
    const icons = popoverElement.querySelectorAll('i[tabindex]');

    if (icons.length > 0) {
      const firstIcon = icons[0] as HTMLElement;
      const lastIcon = icons[icons.length - 1] as HTMLElement;

      firstIcon.focus();

      // Handle enter/space key activation
      icons.forEach(icon => {
        icon.addEventListener('keydown', (event: KeyboardEvent) => {
          if (event.code === 'Enter' || event.code === 'Space') {
            (event.target as HTMLElement).click();
            event.preventDefault();
          }
          // Handle tab navigation
          if (event.code === 'Tab') {
            if (event.shiftKey && icon === firstIcon) {
              event.preventDefault();
              lastIcon.focus();
            } else if (!event.shiftKey && icon === lastIcon) {
              event.preventDefault();
              firstIcon.focus();
            }
          }
        });
      });
    }
  }

  private clickOutsideListener?: (event: Event) => void;

  private setupClickOutsideListener(): void {
    this.clickOutsideListener = (event: Event) => {
      const target = event.target as Element;
      const popoverElement = this.componentRef?.location.nativeElement;
      const triggerButton = this.elementRef.nativeElement.querySelector('button');

      if (popoverElement
          && !popoverElement.contains(target)
          && !triggerButton?.contains(target)) {
        this.closePopover();
      }
    };

    // Add listener with slight delay to avoid immediate closure
    setTimeout(() => {
      document.addEventListener('click', this.clickOutsideListener!);
    }, 100);
  }

  private removeClickOutsideListener(): void {
    if (this.clickOutsideListener) {
      document.removeEventListener('click', this.clickOutsideListener);
      this.clickOutsideListener = undefined;
    }
  }

  @HostListener('keydown.escape')
  onEscapeKey(): void {
    this.closePopover();
  }

  togglePopover(): void {
    if (this.isPopoverOpen) {
      this.closePopover();
    } else {
      this.openPopover();
    }
  }

  getTwitterUrl(): string {
    return this.socialSharingUtils.getTwitterUrl(this.shareText, this.target);
  }

  getLinkedInUrl(): string {
    return this.socialSharingUtils.getLinkedInUrl(this.shareText, this.target);
  }

  checkLightTester(event: Event): void {
    if (this.LightTesterErrorService && this.LightTesterErrorService.show()) {
      event.preventDefault();
    }
  }

  onAnalyticsClick(): void {
    // Analytics will be handled by the analytics-on directive if present
  }

  openTwitter(event: Event): void {
    event.preventDefault();
    this.onAnalyticsClick();
    this.socialSharingUtils.openSocialPopup(this.getTwitterUrl());
  }

  openLinkedIn(event: Event): void {
    event.preventDefault();
    this.onAnalyticsClick();
    this.socialSharingUtils.openSocialPopup(this.getLinkedInUrl());
  }
}
